/**
 * "Decided under this structure" — the org units and product nodes a ratified
 * cycle report was decided under, as they were named THEN, each bridged to
 * what it is NOW: "Platform Eng — now Core Infrastructure".
 *
 * Reads cycle_reports.structure_snapshot (captured at write time, mig 727) and
 * resolves each node forward through the reorg mapping (mig 733). Renders
 * nothing for reports written before structure was captured, or that reference
 * no structure. Spec: PRD-structure-versioning-and-transitions FR-6.
 */

import { useEffect, useMemo, useState } from 'react';
import { Network } from 'lucide-react';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import type { HierarchyKind } from '../../lib/hierarchy/hierarchyTypes';
import {
  resolveStructureNodes,
  structureBridgeLabel,
  structureBridgeMovedTo,
  type ResolvedStructureNode,
} from '../../lib/hierarchy/structureBridge';

interface SnapshotNode {
  id: string;
  kind: HierarchyKind;
  name: string;
  pathNames: string[];
  isScope: boolean;
  itemCount: number;
}

type Rec = Record<string, unknown>;

function readSnapshot(raw: unknown): { capturedAt: string | null; nodes: SnapshotNode[] } {
  const snap = (raw ?? {}) as Rec;
  const byId = new Map<string, SnapshotNode>();
  const add = (n: Rec | null | undefined, isScope: boolean) => {
    if (!n || typeof n.id !== 'string' || n.missing) return;
    const existing = byId.get(n.id);
    if (existing) {
      existing.isScope = existing.isScope || isScope;
      return;
    }
    byId.set(n.id, {
      id: n.id,
      kind: n.kind === 'product' ? 'product' : 'org',
      name: String(n.name ?? ''),
      pathNames: Array.isArray(n.path_names) ? (n.path_names as string[]) : [],
      isScope,
      itemCount: 0,
    });
  };
  add(snap.cycle_scope as Rec | null, true);
  for (const n of Object.values((snap.nodes ?? {}) as Record<string, Rec>)) add(n, false);
  for (const placement of Object.values((snap.item_nodes ?? {}) as Record<string, Rec>)) {
    for (const key of ['org_unit_id', 'product_node_id'] as const) {
      const node = typeof placement[key] === 'string' ? byId.get(placement[key] as string) : undefined;
      if (node) node.itemCount += 1;
    }
  }
  return { capturedAt: typeof snap.captured_at === 'string' ? snap.captured_at : null, nodes: [...byId.values()] };
}

export function StructureAtTheTime({ reportId }: { reportId: string }) {
  const [snapshot, setSnapshot] = useState<{ capturedAt: string | null; nodes: SnapshotNode[] } | null>(null);
  const [resolved, setResolved] = useState<Map<string, ResolvedStructureNode | null>>(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const db = getTypedSupabaseClient();
      const { data, error } = await db.from('cycle_reports').select('structure_snapshot').eq('id', reportId).maybeSingle();
      if (cancelled || error || !data?.structure_snapshot) return;
      const snap = readSnapshot(data.structure_snapshot);
      setSnapshot(snap);
      const merged = new Map<string, ResolvedStructureNode | null>();
      for (const kind of ['org', 'product'] as const) {
        const ids = snap.nodes.filter((n) => n.kind === kind).map((n) => n.id);
        if (ids.length === 0) continue;
        // A bridge is a courtesy: if it cannot be resolved, the historical names still stand.
        const part = await resolveStructureNodes(kind, ids).catch(() => new Map<string, ResolvedStructureNode | null>());
        part.forEach((v, k) => merged.set(k, v));
      }
      if (!cancelled) setResolved(merged);
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const rows = useMemo(
    () => (snapshot?.nodes ?? []).slice().sort((a, b) => Number(b.isScope) - Number(a.isScope) || a.pathNames.join('/').localeCompare(b.pathNames.join('/'))),
    [snapshot],
  );

  if (!snapshot || rows.length === 0) return null;

  return (
    <section className="border border-[var(--rule)] p-5 space-y-3" data-testid="structure-at-the-time">
      <div className="flex items-center gap-2">
        <Network className="h-4 w-4 text-[var(--text-ter)]" />
        <h3 className="font-semibold text-[var(--text-pri)]">Decided under this structure</h3>
      </div>
      <p className="text-sm text-[var(--text-sec)]">
        The organization as it was named when this report was written. Where a unit has since changed, today&apos;s name follows.
      </p>
      <ul className="space-y-1.5 text-sm">
        {rows.map((n) => {
          const r = resolved.get(n.id);
          const bridge = structureBridgeLabel(n.name, r);
          const movedTo = structureBridgeMovedTo(n.pathNames, r);
          return (
            <li key={n.id} className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[var(--text-pri)]">{n.pathNames.length > 0 ? n.pathNames.join(' › ') : n.name}</span>
              {n.isScope && <span className="text-xs text-[var(--text-ter)]">cycle scope</span>}
              {n.itemCount > 0 && (
                <span className="text-xs text-[var(--text-ter)]">
                  {n.itemCount} ranked item{n.itemCount === 1 ? '' : 's'}
                </span>
              )}
              {bridge && <span className="text-[var(--text-ter)]">— {bridge}</span>}
              {!bridge && movedTo && <span className="text-[var(--text-ter)]">— now under {movedTo}</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
